import './index.css'
import cn from 'clsx'

import React, { useCallback, useState } from 'react'
import { keyEqualTo } from '@disco/utils/keyboard'
import { Flex as RadixFlex } from 'components/ds'
import Button from 'components/ui/button'
import Checkbox from 'components/ui/checkbox'
import DiscoModal from 'components/ui/disco-modal'
import Flex from 'components/ui/flex'
import Heading from 'components/ui/heading'
import Icon from 'components/ui/icon'
import { modalWindowStyles } from 'components/utils/modalStyles'
type Action = {
  caption: string
  disabled?: boolean
  progressCaption?: string
  progressIndicator?: boolean
  onClick: () => unknown
}
type Props = {
  children: React.ReactNode
  primaryAction: Action
  secondaryAction?: Action
  status?: 'danger' | 'warning'
  width?: 'small' | 'large'
  title: React.ReactNode
  onCancel?: () => unknown
  onDismiss?: () => void
  notClosable?: boolean
}

/**
 * @deprecated Use `Dialog` from `components/ds` instead. Gradual migration recommended.
 * @see {@link components/ds/Dialog}
 * @migration
 * - Replace with DS Dialog compound component:
 *   ```tsx
 *   // Legacy
 *   <ModalDialog
 *     title="Confirm"
 *     primaryAction={{ caption: 'Delete', onClick: handleDelete }}
 *     status="danger"
 *   >
 *     Are you sure?
 *   </ModalDialog>
 *
 *   // DS alternative
 *   <Dialog.Root open={true}>
 *     <Dialog.Content>
 *       <Dialog.Title>Confirm</Dialog.Title>
 *       <Dialog.Description>Are you sure?</Dialog.Description>
 *       <Flex gap="3" mt="4" justify="end">
 *         <Dialog.Close>
 *           <Button variant="soft">Cancel</Button>
 *         </Dialog.Close>
 *         <Button color="red" onClick={handleDelete}>Delete</Button>
 *       </Flex>
 *     </Dialog.Content>
 *   </Dialog.Root>
 *   ```
 * - Prop mapping:
 *   - title → `<Dialog.Title>`
 *   - children → `<Dialog.Description>` or custom content
 *   - primaryAction/secondaryAction → Compose with DS `<Button>` components
 *   - status → Use `color` prop on action buttons (e.g., color="red" for danger)
 *   - width → Use `maxWidth` prop on Dialog.Content
 *   - onCancel → Wrap cancel button with `<Dialog.Close>`
 *   - notClosable → Set `onOpenChange` to prevent closing
 * - Custom layouts may require composing Dialog.Content with DS layout components
 *
 * A modal dialog component that provides a standardized interface for confirmation and action dialogs.
 * Supports primary and secondary actions, different status types, and customizable content.
 *
 * @param children - The content to display in the modal body
 * @param primaryAction - The main action button configuration with caption and onClick handler
 * @param secondaryAction - Optional secondary action button configuration
 * @param status - The status type that affects styling and icon display ('danger', 'warning', or undefined)
 * @param width - The width variant of the modal (default: 'small')
 * @param title - The title to display in the modal header
 * @param onCancel - Optional callback for cancel button (shows cancel button if provided)
 * @param onDismiss - Optional callback for dismiss functionality (shows "don't show again" checkbox)
 * @param notClosable - Whether the modal can be closed by clicking outside or ESC key
 * @returns A modal dialog with configurable actions and styling
 */
const ModalDialog = ({
  children,
  primaryAction,
  secondaryAction,
  status,
  title,
  onCancel,
  onDismiss,
  width = 'small',
  notClosable,
}: Props): React.ReactNode => {
  let buttonAppearance

  switch (status) {
    case 'danger':
      buttonAppearance = 'danger'
      break

    case 'warning':
      buttonAppearance = 'warning'
      break

    default:
      buttonAppearance = 'primary'
      break
  }

  const [willDismiss, setWillDismiss] = useState<boolean>(false)
  // Note: Removed due to [DISCO-8519] - Undo if primary action selection is required
  //       Radix will likely provide a better way to handle this
  // const setPrimaryActionRef = useCallback((node: ?HTMLElement) => {
  //   if (node) {
  //     node.focus()
  //   }
  // }, [])
  const handlePrimaryAction = useCallback(() => {
    if (willDismiss && onDismiss) {
      onDismiss()
    }

    primaryAction.onClick()
  }, [primaryAction.onClick, onDismiss, willDismiss])
  const handleSecondaryAction = useCallback(() => {
    if (willDismiss && onDismiss) {
      onDismiss()
    }

    if (secondaryAction?.onClick) {
      secondaryAction.onClick()
    }
  }, [secondaryAction?.onClick, onDismiss, willDismiss])
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (keyEqualTo(event.nativeEvent, 'Enter')) {
        event.preventDefault()
        event.stopPropagation()
        handlePrimaryAction()
      }
    },
    [handlePrimaryAction],
  )
  return (
    <DiscoModal
      style={{
        ...modalWindowStyles,
        overlay: { ...modalWindowStyles.overlay, overflow: 'auto' },
      }}
      onRequestClose={
        notClosable ? undefined : onCancel || primaryAction.onClick
      }
    >
      <div
        className={cn('ModalDialog', {
          'ModalDialog--danger': status === 'danger',
          'ModalDialog--warning': status === 'warning',
          'ModalDialog--small': width === 'small',
          'ModalDialog--large': width === 'large',
        })}
        onKeyDown={handleKeyDown}
      >
        <div className="ModalDialog-header">
          <Flex spacing="small">
            {Boolean(status) && (
              <div className="ModalDialog-icon">
                <Icon type="warning" />
              </div>
            )}
            <Flex.Content>
              <Heading size={600}>{title}</Heading>
            </Flex.Content>
          </Flex>
        </div>
        <div className="ModalDialog-content">
          {children}
          {onDismiss && (
            <Checkbox
              checked={willDismiss}
              label="Don’t show this message again"
              onChange={(label, checked) => setWillDismiss(checked)}
            />
          )}
        </div>
        <div className="ModalDialog-actions">
          <RadixFlex justify="end" gap="2">
            {onCancel && <Button caption="Cancel" onClick={onCancel} />}

            {secondaryAction && (
              <Button {...secondaryAction} onClick={handleSecondaryAction} />
            )}
            {primaryAction && (
              <Button
                {...primaryAction}
                appearance={buttonAppearance} // ref={setPrimaryActionRef}
                onClick={handlePrimaryAction}
              />
            )}
          </RadixFlex>
        </div>
      </div>
    </DiscoModal>
  )
}

export default ModalDialog
